import { useEffect, useRef } from 'react';
import { fetchPriceData } from '@/api/agency-quote-api';
import { useQuoteStore } from '@/hooks/quote/use-quote-store';
import { useQuote } from '@/hooks/use-quote';
import { getRouteStep } from '@/services/location-service';
import { reportError } from '@/services/error-reporting-service';

const MAX_POLLS = 150;
const POLL_INTERVAL_MS = 2_000;

/** Polls for price data while the quote is actively being rated. */
export const usePricePolling = () => {
  const quote = useQuote();
  const hydrateFromStepData = useQuoteStore((s) => s.hydrateFromStepData);

  const isQuoting = quote?.details.quote_status === 'quoting';

  const pollCountRef = useRef(0);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!isQuoting) return;

    pollCountRef.current = 0;
    inFlightRef.current = false;

    const interval = setInterval(() => {
      if (inFlightRef.current) return;

      if (pollCountRef.current >= MAX_POLLS) {
        clearInterval(interval);
        return;
      }

      pollCountRef.current += 1;
      inFlightRef.current = true;

      const { entity_index, step_id } = getRouteStep();

      fetchPriceData(entity_index, step_id)
        .then((priceData) => {
          if (!priceData) return;

          hydrateFromStepData(null, priceData);

          if (priceData.details.quote_status !== 'quoting') {
            clearInterval(interval);
          }
        })
        .catch((error: unknown) => {
          reportError({ error });
        })
        .finally(() => {
          inFlightRef.current = false;
        });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isQuoting, hydrateFromStepData]);
};
